<script setup lang="ts">
import BankTransferInfo from '../../account/wallet/BankTransferInfo.vue'
import { useInvestmentProcessDataState } from '../../../utilities/investment-process/useInvestmentProcess'
import { CreditCardIcon, SwitchHorizontalIcon } from '@heroicons/vue/outline'
import { getCampaignData } from '../../../utilities/investment-process/useCampaign'
import BaseButtonMuted from '../../base/BaseButtonMuted.vue'
import { OrderPaymentRequest, postPayment } from '../../../utilities/api'
import { getWalletLink } from '../../../utilities/useLinks'
import { openAlertDialog } from '../../../utilities/useDialog'
import { i18n } from '../../../localization'
import { ref } from 'vue'

const investmentProcessStore = useInvestmentProcessDataState()

const payViaCreditCardLoading = ref(false)
const campaignData = getCampaignData()

withDefaults(
    defineProps<{
        showCreditCard: boolean
        showWireTransfer: boolean
    }>(),
    {
        showCreditCard: false,
        showWireTransfer: false,
    }
)

async function payViaCreditCard() {
    const paymentRequest: OrderPaymentRequest = {
        amount: investmentProcessStore.amountToPay,
        order_id: investmentProcessStore.createdOrder.order_uid,
        payment_method: 'credit_card',
        lang: 'de',
        return_url: getWalletLink() + '?payment_status=success',
        cancel_url: getWalletLink() + '?payment_status=canceled',
        error_url: getWalletLink() + '?payment_status=error',
    }
    try {
        payViaCreditCardLoading.value = true
        const { data } = await postPayment(paymentRequest)
        window.location.href = data.redirectUrl
    } catch (error) {
        openAlertDialog(i18n.global.t('errors.general_error'), error.response?.data?.message)
    } finally {
        payViaCreditCardLoading.value = false
    }
}
</script>

<template>
    <div v-if="investmentProcessStore.createdOrder" class="space-y-4">
        <div
            v-if="
                investmentProcessStore.createdOrder.payment_methods.wire_transfer === true &&
                showWireTransfer &&
                !campaignData?.asset_data.hide_payment_info
            "
            class="overflow-hidden rounded-lg border">
            <div class="px-6 py-4">
                <h3 class="flex text-xl font-bold text-gray-900">
                    <SwitchHorizontalIcon class="mr-2 w-5" />
                    {{ $t('investment_process.pay_via_bank_transfer_header') }}
                </h3>
                <p class="mt-2 font-medium text-gray-500">
                    {{ $t('investment_process.pay_via_bank_transfer_description') }}
                </p>
            </div>
            <BankTransferInfo
                v-if="!campaignData?.asset_data.hide_payment_info"
                :amount="investmentProcessStore.amountToPay"
                :account_holder="investmentProcessStore.createdOrder.bank_account_holder"
                :iban="campaignData?.asset_data.iban"
                :bic="campaignData?.asset_data.bic"
                :payment_reference="investmentProcessStore.createdOrder.reference_number"
                :currency="campaignData?.asset_data.currency"
                class="border-t" />
        </div>
        <div
            v-if="
                investmentProcessStore.createdOrder.payment_methods.credit_card === true &&
                showCreditCard &&
                !campaignData?.asset_data.hide_payment_info
            "
            class="overflow-hidden rounded-lg border">
            <div class="px-6 py-4">
                <h3 class="flex text-xl font-bold text-gray-900">
                    <CreditCardIcon class="mr-2 w-5" />
                    {{ $t('investment_process.pay_via_credit_card_header') }}
                </h3>
                <p class="mt-2 font-medium text-gray-500">
                    {{ $t('investment_process.pay_via_credit_card_description') }}
                </p>
                <BaseButtonMuted
                    class="mt-4"
                    :class="{ 'pointer-events-none opacity-50': !investmentProcessStore.allSignaturesReadyForPayment }"
                    size="lg"
                    :loading="payViaCreditCardLoading"
                    @click="payViaCreditCard">
                    {{ $t('investment_process.pay_via_credit_card_button') }}
                </BaseButtonMuted>
            </div>
        </div>
        <div
            v-if="
                investmentProcessStore.createdOrder.payment_methods.credit_card === false &&
                investmentProcessStore.createdOrder.payment_methods.wire_transfer === false &&
                campaignData?.asset_data.iban &&
                campaignData?.asset_data.bic &&
                !campaignData?.asset_data.hide_payment_info
            "
            class="overflow-hidden rounded-lg border">
            <div class="px-6 py-4">
                <h3 class="mb-4 flex text-xl font-bold text-gray-900">
                    {{ $t('investment_process.payment_details') }}
                </h3>
                <div class="mb-4 flex justify-between">
                    <div>IBAN</div>
                    <div>{{ getCampaignData().asset_data.iban }}</div>
                </div>
                <div class="flex justify-between">
                    <div>BIC</div>
                    <div>{{ getCampaignData().asset_data.bic }}</div>
                </div>
            </div>
        </div>
    </div>
</template>
